/**
 * Label-key derivation for a bot template content key — moved out of the deleted
 * `model/templateCatalog.ts` static registry (templates doc §13.2, §13.13). The catalogue now
 * supplies the key set, group and order; this module supplies only the i18n key derivation the
 * static registry used to hard-code, shared between the templates page and the chatbot settings
 * page's required-texts card (settings doc §17.4).
 *
 * Derivation: strip the `bot.` prefix, then camel-case every `.x` / `-x` into `X` (underscores are
 * left alone — `contact.confirmation_prompt` → `contactConfirmation_prompt`, matching the shipped
 * `chatbot.json` key `templates.keys.contactConfirmation_prompt`). A key with no matching i18n entry
 * renders its raw content key in mono, never a raw i18n key path (templates doc §13.2).
 */

/**
 * `bot.sales-mode.pre-opening` → `salesModePreOpening`. Also reused for the escalation
 * dictionary's dotted tool names (`orders.getStatus` → `ordersGetStatus`) — i18next's default
 * `keySeparator` would otherwise treat a raw dotted tool name appended to an i18n key path as
 * further nesting rather than a literal segment, missing the translation entirely.
 */
export function templateLabelSegment(contentKey: string): string {
  const withoutPrefix = contentKey.startsWith('bot.') ? contentKey.slice(4) : contentKey;
  return withoutPrefix.replace(/[.-](\w)/g, (_match, char: string) => char.toUpperCase());
}

export interface TemplateLabelKeys {
  labelKey: string;
  descriptionKey: string;
}

/** The two i18n keys (`chatbot:templates.keys.<segment>.label|.description`) for a `FIXED` key. */
export function templateLabelKeys(contentKey: string): TemplateLabelKeys {
  const segment = templateLabelSegment(contentKey);
  return {
    labelKey: `templates.keys.${segment}.label`,
    descriptionKey: `templates.keys.${segment}.description`,
  };
}

/** Group label key — `templates.groups.<code>` (unknown code renders raw, templates doc §13.2). */
export function templateGroupLabelKey(groupCode: string): string {
  return `templates.groups.${groupCode}`;
}
